import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { roleLabel } from '../lib/roleLabel';
import { PageHeader, ErrorState, StatusPill } from '@ui-kit';
import { useUserDetail } from '../hooks/useUserDetail';
import { useUpdateUser } from '../hooks/useUpdateUser';
import { useDeleteUser } from '../hooks/useDeleteUser';
import { useChangeUserRole } from '../hooks/useChangeUserRole';
import { useLockUser } from '../hooks/useLockUser';
import { useUnlockUser } from '../hooks/useUnlockUser';
import { useAssignableRoles } from '../hooks/useAssignableRoles';
import { UserDeleteConfirm } from '../components/UserDeleteConfirm';
import { UserRoleChangeConfirm } from '../components/UserRoleChangeConfirm';
import { UserRoleReleaseConfirm } from '../components/UserRoleReleaseConfirm';
import { UserLockToggle } from '../components/UserLockToggle';
import { CommonUsagePanel } from '../components/CommonUsagePanel';
import { useMe } from '../../_shared/hooks/useMe';
import { useHasPermission } from '../../_shared/hooks/useCurrentUser';
import { ButtonGuard } from '../../_shared/components/ButtonGuard';
import type { ApiError } from '../types/user';

function formatDate(isoString: string | null | undefined): string {
  if (!isoString) return '-';
  return new Date(isoString).toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const userId = id ? parseInt(id, 10) : undefined;
  const meQuery = useMe();
  const currentUserId = meQuery.data?.id;
  const isSelf = userId !== undefined && userId === currentUserId;
  const hasUserWrite = useHasPermission('USER_WRITE');

  const detailQuery = useUserDetail(userId);
  const user = detailQuery.data;

  const rolesQuery = useAssignableRoles();
  const assignableRoles = rolesQuery.data ?? [];

  const updateMutation = useUpdateUser(userId ?? 0);
  const deleteMutation = useDeleteUser();
  const changeRoleMutation = useChangeUserRole(userId ?? 0);
  const lockMutation = useLockUser(userId ?? 0);
  const unlockMutation = useUnlockUser(userId ?? 0);

  // 편집 state
  const [displayName, setDisplayName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [selectedRole, setSelectedRole] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [updateError, setUpdateError] = useState<string | undefined>();
  const [successMessage, setSuccessMessage] = useState<string | undefined>();

  // 다이얼로그 state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | undefined>();
  const [roleError, setRoleError] = useState<string | undefined>();
  const [releaseError, setReleaseError] = useState<string | undefined>();
  const [lockError, setLockError] = useState<string | undefined>();

  // 편집 시작
  const handleStartEdit = useCallback(() => {
    if (!user) return;
    setDisplayName(user.displayName);
    setEnabled(user.enabled);
    setSelectedRole(user.role);
    setEditMode(true);
    setUpdateError(undefined);
  }, [user]);

  // 저장
  const handleSave = useCallback(() => {
    if (!user) return;
    setUpdateError(undefined);
    updateMutation.mutate(
      { displayName: displayName.trim() || undefined, enabled },
      {
        onSuccess: () => {
          setEditMode(false);
          setSuccessMessage(t('user.toast.updated'));
          setTimeout(() => setSuccessMessage(undefined), 4500);
        },
        onError: (err: unknown) => {
          const apiErr = err as ApiError;
          setUpdateError(apiErr.message ?? '수정에 실패했습니다');
        },
      }
    );
  }, [user, displayName, enabled, updateMutation, t]);

  // 역할 변경 확인
  const handleRoleChange = useCallback(() => {
    if (!user || !selectedRole || selectedRole === user.role) return;
    setRoleDialogOpen(true);
    setRoleError(undefined);
  }, [user, selectedRole]);

  const handleRoleConfirm = useCallback(() => {
    if (!user) return;
    setRoleError(undefined);
    changeRoleMutation.mutate(
      { role: selectedRole },
      {
        onSuccess: () => {
          setRoleDialogOpen(false);
          setEditMode(false);
          setSuccessMessage(t('user.toast.roleChanged'));
          setTimeout(() => setSuccessMessage(undefined), 4500);
        },
        onError: (err: unknown) => {
          const apiErr = err as ApiError;
          setRoleError(apiErr.message ?? '역할 변경에 실패했습니다');
        },
      }
    );
  }, [user, selectedRole, changeRoleMutation, t]);

  // 역할 해제 — ROLE_USER 로 강등
  const handleReleaseConfirm = useCallback(() => {
    if (!user) return;
    setReleaseError(undefined);
    changeRoleMutation.mutate(
      { role: 'ROLE_USER' },
      {
        onSuccess: () => {
          setReleaseDialogOpen(false);
          setSuccessMessage(t('user.toast.roleReleased'));
          setTimeout(() => setSuccessMessage(undefined), 4500);
        },
        onError: (err: unknown) => {
          const apiErr = err as ApiError;
          // USER-403-001: 자기 자신 강등 시도
          const errorKey = `user.error.${apiErr.code}`;
          const resolved = t(errorKey) !== errorKey ? t(errorKey) : (apiErr.message ?? '역할 해제에 실패했습니다');
          setReleaseError(resolved);
        },
      }
    );
  }, [user, changeRoleMutation, t]);

  // 삭제 확인
  const handleDeleteConfirm = useCallback(() => {
    if (!userId) return;
    setDeleteError(undefined);
    deleteMutation.mutate(userId, {
      onSuccess: () => {
        navigate('/users', { replace: true });
      },
      onError: (err: unknown) => {
        const apiErr = err as ApiError;
        setDeleteError(apiErr.message ?? '삭제에 실패했습니다');
      },
    });
  }, [userId, deleteMutation, navigate]);

  // 잠금/해제 확인
  const handleLockToggle = useCallback(() => {
    if (!user) return;
    setLockError(undefined);
    const onError = (err: unknown) => {
      const apiErr = err as ApiError;
      setLockError(apiErr.message ?? '작업에 실패했습니다');
    };
    const onSuccess = () => {
      setLockDialogOpen(false);
      setSuccessMessage(user.locked ? t('user.toast.unlocked') : t('user.toast.locked'));
      setTimeout(() => setSuccessMessage(undefined), 4500);
    };

    if (user.locked) {
      unlockMutation.mutate(undefined, { onSuccess, onError });
    } else {
      lockMutation.mutate(undefined, { onSuccess, onError });
    }
  }, [user, lockMutation, unlockMutation, t]);

  const btnPrimary: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    padding: 'var(--spacing-2) var(--spacing-4)',
    background: 'var(--color-brand-primary)',
    color: 'var(--color-text-inverse)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
  };

  const btnSecondary: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    padding: 'var(--spacing-2) var(--spacing-4)',
    background: 'var(--color-surface-elevated)',
    color: 'var(--color-text-primary)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
  };

  const btnDanger: React.CSSProperties = {
    ...btnSecondary,
    color: 'var(--color-danger)',
    borderColor: 'var(--color-danger)',
  };

  const btnWarning: React.CSSProperties = {
    ...btnSecondary,
    color: 'var(--color-warning)',
    borderColor: 'var(--color-warning)',
  };

  const fieldRow: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '140px 1fr',
    gap: 'var(--spacing-4)',
    alignItems: 'center',
    padding: 'var(--spacing-3) 0',
    borderBottom: '1px solid var(--color-surface-border)',
  };

  const fieldLabel: React.CSSProperties = {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    fontWeight: 500,
  };

  const inputStyle: React.CSSProperties = {
    padding: 'var(--spacing-2) var(--spacing-3)',
    background: 'var(--color-surface-elevated)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-sans)',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  };

  // loading skeleton
  if (detailQuery.isLoading) {
    return (
      <div style={{ padding: 'var(--spacing-6)' }}>
        <div style={{ height: 32, background: 'var(--color-surface-elevated)', borderRadius: 'var(--radius-sm)', width: '30%', marginBottom: 'var(--spacing-6)' }} />
        <div style={{ background: 'var(--color-surface-background)', border: '1px solid var(--color-surface-border)', borderRadius: 'var(--radius-md)', padding: 'var(--spacing-6)' }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} style={{ ...fieldRow }}>
              <div style={{ height: 14, background: 'var(--color-surface-elevated)', borderRadius: 'var(--radius-sm)', width: '60%' }} />
              <div style={{ height: 14, background: 'var(--color-surface-elevated)', borderRadius: 'var(--radius-sm)', width: '40%' }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // error
  if (detailQuery.isError) {
    return (
      <div style={{ padding: 'var(--spacing-6)' }}>
        <button
          type="button"
          onClick={() => navigate('/users')}
          style={{ ...btnSecondary, marginBottom: 'var(--spacing-4)' }}
        >
          &larr; {t('user.detail.back')}
        </button>
        <ErrorState
          title={t('user.detail.error.title')}
          message={(detailQuery.error as unknown as ApiError)?.message ?? t('user.error.network')}
          onRetry={() => void detailQuery.refetch()}
        />
      </div>
    );
  }

  if (!user) return null;

  // 역할 해제 버튼 표시 조건:
  // - USER_WRITE 권한 보유
  // - 자기 자신이 아님
  // - 이미 ROLE_USER 가 아님 (강등 의미 없음)
  // - ROLE_EXEC 는 해제 대상에서 제외 (exec 계정은 별도 관리)
  const showReleaseButton =
    hasUserWrite &&
    !isSelf &&
    user.role !== 'ROLE_USER' &&
    user.role !== 'ROLE_EXEC';

  return (
    <div style={{ padding: 'var(--spacing-6)' }}>
      {/* 성공 메시지 */}
      {successMessage && (
        <div
          role="status"
          style={{
            marginBottom: 'var(--spacing-4)',
            padding: 'var(--spacing-3)',
            background: 'color-mix(in srgb, var(--color-success) 12%, var(--color-surface-muted))',
            border: '1px solid var(--color-success)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-primary)',
          }}
        >
          {successMessage}
        </div>
      )}

      {/* 에러 메시지 */}
      {updateError && (
        <div
          role="alert"
          style={{
            marginBottom: 'var(--spacing-4)',
            padding: 'var(--spacing-3)',
            background: 'color-mix(in srgb, var(--color-danger) 12%, var(--color-surface-muted))',
            border: '1px solid var(--color-danger)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-danger)',
          }}
        >
          {updateError}
        </div>
      )}

      {/* 페이지 헤더 */}
      <PageHeader
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            {t('user.detail.title')}
            {/* 읽기 전용 배지 — USER_WRITE 권한 없는 경우 */}
            {!hasUserWrite && (
              <StatusPill tone="neutral">
                {t('user.badge.readonly')}
              </StatusPill>
            )}
          </span>
        }
        breadcrumb={
          <span>
            <button
              type="button"
              onClick={() => navigate('/users')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', padding: 0 }}
            >
              {t('user.list.title')}
            </button>
            {' > '}
            {user.displayName}
          </span>
        }
        actions={
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
            {!editMode ? (
              <>
                {/* 역할 해제 버튼 */}
                {showReleaseButton && (
                  <ButtonGuard requirePermission="USER_WRITE">
                    {({ disabled, reason }) => (
                      <button
                        type="button"
                        onClick={() => {
                          if (disabled) return;
                          setReleaseError(undefined);
                          setReleaseDialogOpen(true);
                        }}
                        disabled={disabled}
                        title={disabled ? reason : t('user.detail.action.releaseRole.tooltip')}
                        style={{
                          ...btnWarning,
                          fontSize: 'var(--font-size-sm)',
                          ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                        }}
                        aria-label={t('user.detail.action.releaseRole')}
                      >
                        {t('user.detail.action.releaseRole')}
                      </button>
                    )}
                  </ButtonGuard>
                )}
                <button type="button" onClick={handleStartEdit} style={btnSecondary}>
                  {t('user.detail.action.edit')}
                </button>
                <button
                  type="button"
                  disabled={isSelf}
                  title={isSelf ? t('user.action.self.disabled.tooltip') : undefined}
                  onClick={() => {
                    setDeleteError(undefined);
                    setDeleteDialogOpen(true);
                  }}
                  style={{
                    ...btnDanger,
                    opacity: isSelf ? 0.5 : 1,
                    cursor: isSelf ? 'not-allowed' : 'pointer',
                  }}
                  aria-disabled={isSelf}
                >
                  {t('user.detail.action.delete')}
                </button>
              </>
            ) : (
              <>
                <button type="button" onClick={() => setEditMode(false)} style={btnSecondary}>
                  {t('user.form.button.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  style={btnPrimary}
                >
                  {updateMutation.isPending ? t('user.form.button.loading') : t('user.detail.action.save')}
                </button>
              </>
            )}
          </div>
        }
      />

      {/* 잠금 상태 배너 */}
      {user.locked && (
        <div
          role="alert"
          style={{
            marginTop: 'var(--spacing-4)',
            padding: 'var(--spacing-3)',
            background: 'color-mix(in srgb, var(--color-danger) 10%, var(--color-surface-muted))',
            border: '1px solid var(--color-danger)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-danger)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span>
            {user.lockedAt
              ? t('user.detail.field.locked.since', { since: formatDate(user.lockedAt) })
              : t('user.detail.locked.banner')}
          </span>
          <button
            type="button"
            disabled={isSelf}
            title={isSelf ? t('user.action.self.disabled.tooltip') : undefined}
            onClick={() => {
              setLockError(undefined);
              setLockDialogOpen(true);
            }}
            style={{
              ...btnSecondary,
              opacity: isSelf ? 0.5 : 1,
              cursor: isSelf ? 'not-allowed' : 'pointer',
            }}
          >
            {t('user.detail.action.unlock')}
          </button>
        </div>
      )}

      {/* 메인 폼 카드 */}
      <div
        style={{
          marginTop: 'var(--spacing-6)',
          background: 'var(--color-surface-background)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--spacing-6)',
        }}
      >
        {/* 이메일 (읽기 전용) */}
        <div style={fieldRow}>
          <label style={fieldLabel}>{t('user.detail.field.email')}</label>
          <div>
            <span
              style={{
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)',
              }}
            >
              {user.email}
            </span>
            <p style={{ margin: '2px 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
              {t('user.detail.field.email.hint')}
            </p>
          </div>
        </div>

        {/* 이름 */}
        <div style={fieldRow}>
          <label htmlFor="detail-displayName" style={fieldLabel}>
            {t('user.detail.field.displayName')}
          </label>
          {editMode ? (
            <input
              id="detail-displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              style={inputStyle}
              maxLength={100}
            />
          ) : (
            <span style={{ fontSize: 'var(--font-size-sm)' }}>{user.displayName}</span>
          )}
        </div>

        {/* 역할 — 동적 로드 */}
        <div style={fieldRow}>
          <label htmlFor="detail-role" style={fieldLabel}>
            {t('user.detail.field.role')}
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', flexWrap: 'wrap' }}>
            {editMode ? (
              <>
                <select
                  id="detail-role"
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  disabled={isSelf || rolesQuery.isLoading}
                  title={isSelf ? t('user.detail.role.selfDisabled') : undefined}
                  style={{
                    ...inputStyle,
                    width: 'auto',
                    opacity: isSelf || rolesQuery.isLoading ? 0.5 : 1,
                    cursor: isSelf ? 'not-allowed' : 'pointer',
                  }}
                  aria-busy={rolesQuery.isLoading}
                  aria-disabled={isSelf}
                >
                  {rolesQuery.isLoading && (
                    <option value="">{t('user.role.loading')}</option>
                  )}
                  {!rolesQuery.isLoading && assignableRoles.length === 0 && (
                    <option value={selectedRole}>{selectedRole}</option>
                  )}
                  {!rolesQuery.isLoading &&
                    assignableRoles.map((r) => (
                      <option key={r.code} value={r.code}>
                        {roleLabel(r, i18n.language)}
                      </option>
                    ))}
                </select>
                {selectedRole !== user.role && !isSelf && (
                  <button
                    type="button"
                    onClick={handleRoleChange}
                    style={{
                      ...btnSecondary,
                      fontSize: 'var(--font-size-xs)',
                      padding: 'var(--spacing-1) var(--spacing-3)',
                    }}
                  >
                    {t('user.detail.action.changeRole')}
                  </button>
                )}
                {isSelf && (
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                    {t('user.detail.role.selfDisabled')}
                  </span>
                )}
              </>
            ) : (
              <span
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 'var(--font-size-xs)',
                  padding: '1px var(--spacing-2)',
                  borderRadius: 'var(--radius-sm)',
                  border: `1px solid ${
                    user.role === 'ROLE_ADMIN'
                      ? 'var(--color-brand-primary)'
                      : user.role === 'ROLE_MANAGER'
                      ? 'var(--color-info)'
                      : 'var(--color-surface-border)'
                  }`,
                  color:
                    user.role === 'ROLE_ADMIN'
                      ? 'var(--color-brand-primary)'
                      : user.role === 'ROLE_MANAGER'
                      ? 'var(--color-info)'
                      : 'var(--color-text-secondary)',
                  background: 'var(--color-surface-elevated)',
                }}
              >
                {t(`appshell.user.role.${user.role.replace('ROLE_', '').toLowerCase()}`, { defaultValue: user.role })}
              </span>
            )}
          </div>
        </div>

        {/* 활성 */}
        <div style={fieldRow}>
          <label style={fieldLabel}>{t('user.detail.field.enabled')}</label>
          {editMode ? (
            <div style={{ display: 'flex', gap: 'var(--spacing-3)' }}>
              <label
                style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}
              >
                <input
                  type="radio"
                  name="enabled"
                  checked={enabled}
                  onChange={() => setEnabled(true)}
                />
                {t('user.status.active')}
              </label>
              <label
                style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}
              >
                <input
                  type="radio"
                  name="enabled"
                  checked={!enabled}
                  onChange={() => setEnabled(false)}
                />
                {t('user.status.inactive')}
              </label>
            </div>
          ) : (
            user.enabled ? (
              <StatusPill tone="success">{t('user.status.active')}</StatusPill>
            ) : (
              <StatusPill tone="neutral">{t('user.status.inactive')}</StatusPill>
            )
          )}
        </div>

        {/* 잠금 상태 */}
        <div style={fieldRow}>
          <label style={fieldLabel}>{t('user.detail.field.locked')}</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
            {user.locked ? (
              <StatusPill tone="danger">
                {user.lockedAt
                  ? t('user.detail.field.locked.since', { since: formatDate(user.lockedAt) })
                  : t('user.status.locked')}
              </StatusPill>
            ) : (
              <StatusPill tone="success">{t('user.detail.field.locked.normal')}</StatusPill>
            )}
            {!editMode && (
              <button
                type="button"
                disabled={isSelf}
                title={isSelf ? t('user.action.self.disabled.tooltip') : undefined}
                onClick={() => {
                  setLockError(undefined);
                  setLockDialogOpen(true);
                }}
                style={{
                  ...(user.locked ? btnSecondary : { ...btnSecondary, color: 'var(--color-warning)', borderColor: 'var(--color-warning)' }),
                  fontSize: 'var(--font-size-xs)',
                  padding: 'var(--spacing-1) var(--spacing-3)',
                  opacity: isSelf ? 0.5 : 1,
                  cursor: isSelf ? 'not-allowed' : 'pointer',
                }}
                aria-disabled={isSelf}
              >
                {user.locked ? t('user.detail.action.unlock') : t('user.detail.action.lock')}
              </button>
            )}
          </div>
        </div>

        {/* 마지막 로그인 */}
        <div style={fieldRow}>
          <label style={fieldLabel}>{t('user.detail.field.lastLogin')}</label>
          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
            {formatDate(user.lastLoginAt)}
          </span>
        </div>
      </div>

      {/* 감사 정보 */}
      <div
        style={{
          marginTop: 'var(--spacing-4)',
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--spacing-4)',
        }}
      >
        <h3 style={{ margin: '0 0 var(--spacing-3)', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)' }}>
          {t('user.detail.audit.title')}
        </h3>
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
          <span>{t('user.detail.audit.created', { at: formatDate(user.createdAt), by: user.createdBy })}</span>
          <span>{t('user.detail.audit.updated', { at: formatDate(user.updatedAt), by: user.updatedBy })}</span>
          {user.version !== undefined && <span>{t('user.detail.audit.version', { version: user.version })}</span>}
        </div>
      </div>

      {/* 관련 탭 */}
      <div
        style={{
          marginTop: 'var(--spacing-4)',
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--spacing-4)',
        }}
      >
        <h3 style={{ margin: '0 0 var(--spacing-3)', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)' }}>
          {t('user.detail.related.title')}
        </h3>
        <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
          {[t('user.detail.relatedTab.recentLogin'), t('user.detail.relatedTab.activeSessions'), t('user.detail.relatedTab.auditLog')].map((label) => (
            <button
              key={label}
              type="button"
              style={btnSecondary}
              onClick={() => {}}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Dialogs */}
      <UserDeleteConfirm
        open={deleteDialogOpen}
        email={user.email}
        isSelf={isSelf}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeleteError(undefined);
        }}
        onConfirm={handleDeleteConfirm}
        isLoading={deleteMutation.isPending}
        errorMessage={deleteError}
      />

      <UserRoleChangeConfirm
        open={roleDialogOpen}
        email={user.email}
        currentRole={user.role}
        targetRole={selectedRole || user.role}
        isSelf={isSelf}
        onClose={() => {
          setRoleDialogOpen(false);
          setRoleError(undefined);
        }}
        onConfirm={handleRoleConfirm}
        isLoading={changeRoleMutation.isPending}
        errorMessage={roleError}
      />

      <UserRoleReleaseConfirm
        open={releaseDialogOpen}
        email={user.email}
        currentRole={user.role}
        onClose={() => {
          setReleaseDialogOpen(false);
          setReleaseError(undefined);
        }}
        onConfirm={handleReleaseConfirm}
        isLoading={changeRoleMutation.isPending}
        errorMessage={releaseError}
      />

      <UserLockToggle
        open={lockDialogOpen}
        email={user.email}
        isLocked={user.locked}
        isSelf={isSelf}
        onClose={() => {
          setLockDialogOpen(false);
          setLockError(undefined);
        }}
        onConfirm={handleLockToggle}
        isLoading={lockMutation.isPending || unlockMutation.isPending}
        errorMessage={lockError}
      />

      {/* §0.4 사용 공통 모듈 패널 (R7-059) */}
      <CommonUsagePanel />
    </div>
  );
}
